import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Layers, Plug, Bot, Rocket, Activity, Award, Share2, CalendarClock, Dna, Briefcase,
  MessageSquare, Gavel, Syringe, Users, ArrowRight, Building2, HeartPulse, ShoppingBag,
  GraduationCap, Radiation, Dna as DnaIcon, ShieldCheck, Quote, Phone, Mail
} from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import CustomerLogos from '@/components/common/CustomerLogos'

// --- UI shims ---
const Card = ({ className = "", children }: { className?: string; children: React.ReactNode }) => (
  <div className={`rounded-2xl border shadow-sm ${className}`}>{children}</div>
);
const CardContent = ({ className = "", children }: { className?: string; children: React.ReactNode }) => (
  <div className={className}>{children}</div>
);

function SectionWrapper({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      viewport={{ once: true, amount: 0.2 }}
    >
      {children}
      <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
    </motion.div>
  );
}

// AI Head Image Component
const HeadImg = ({ className = "" }) => (
  <img
    alt="AI head silhouette with colorful brain"
    className={className}
    src="/AiBrain.png"
  />
);

const Home: React.FC = () => {
  const { t } = useTranslation()
  const navigationStore = useNavigationStore()

  const whyItems = [
    { title: "Modular & Scalable", desc: "Start small and expand as your needs grow, with flexible modules that scale with your organization.", Icon: Layers },
    { title: "Seamless Integration (FHIR, HL7, APIs)", desc: "Works with your existing systems using industry-standard integrations.", Icon: Plug },
    { title: "AI-powered Automation", desc: "Automate workflows, triage, and scheduling with intelligent tools to save staff time.", Icon: Bot },
    { title: "Fast to Deploy", desc: "Get up and running quickly with streamlined onboarding and configuration.", Icon: Rocket },
    { title: "Built for Healthcare Complexity", desc: "Designed specifically to meet the unique needs of healthcare organizations worldwide.", Icon: Activity },
    { title: "Proven Track Record", desc: "Trusted by leading healthcare systems and organizations around the world.", Icon: Award },
  ];

  const products = [
    { key: "referralcx", name: "ReferralCX", desc: "Manage internal and external referrals with configurable workflows and triage.", Icon: Share2 },
    { key: "schedulingcx", name: "SchedulingCX", desc: "Flexible scheduling for patients, providers, resources, and locations.", Icon: CalendarClock },
    { key: "cellcx", name: "CellCX", desc: "Purpose-built for cell therapy: patient flow, lab coordination, and trial workflows.", Icon: Dna, badge: "" },
    { key: "casecx", name: "CaseCX", desc: "Track complex cases end-to-end with roles, SLAs, and audit-ready history.", Icon: Briefcase },
    { key: "econsultcx", name: "eConsultCX", desc: "Enable virtual consults with secure collaboration and structured feedback.", Icon: MessageSquare },
    { key: "probationcx", name: "ProbationCX", desc: "Simplify referrals to court-ordered programs and monitor outcomes.", Icon: Gavel, badge: "new"  },
    { key: "vaxmanagercx", name: "VaxManagerCX", desc: "Plan, coordinate, and report on vaccination programs at scale.", Icon: Syringe },
    { key: "consumercx", name: "ConsumerCX", desc: "Engage patients and consumers with portals, reminders, and self-service.", Icon: Users },
  ];

  const whoItems = [
    { name: "Hospitals", Icon: Building2 },
    { name: "Public & Private Health", Icon: HeartPulse },
    { name: "Insurers", Icon: ShieldCheck },
    { name: "Cancer Centers", Icon: Radiation },
    { name: "Cell Therapy Centers", Icon: DnaIcon },
    { name: "Retail Clinics", Icon: ShoppingBag },
    { name: "Employers", Icon: Briefcase },
    { name: "Corrections", Icon: Gavel },
    { name: "Education", Icon: GraduationCap },
  ];

  const customerLogoUids = ["rady", "parkland", "dfci", "nhs", "ahs"];
  const testimonials = [
    {
      quote: "International referrals are no longer an administrative headache. EceptionistCX helped us expand our reach and create a new revenue stream while ensuring patients get timely access to care.",
      name: "CHI St. Luke’s",
      title: "Executive Director of International Services",
      logo: "/Image/CustomerLogo/STLUKE.png"
    },
    {
      quote: "Eceptionist worked closely with us to meet the very complicated scheduling and workflow needs of our various programs. Their ability to allow clinical schedulers to directly book laboratory processing and requests that require a series of appointments to be booked over a single day, or weeks and months will allow us to better schedule, utilize and grow our capabilities over time.",
      name: "Olive Sturtevant, BA, MT (ASCP), SBB, SLS, MHP",
      title: "Administrative Director of CMCF – Dana Farber Cancer Institute",
      logo: "/Image/CustomerLogo/DFCI.png"
    },
    {
      quote: "The referral process for specialty pediatrics is now seamless. Families feel supported, and our providers spend more time on care rather than chasing paperwork.",
      name: "CHOC Children’s Hospital",
      title: "Director of Care Coordination",
      logo: "/Image/CustomerLogo/Rady.svg"
    },
    {
      quote: "Eceptionist worked with us to understand our workflow. Now we’re able to provide patients with better service while ensuring those who need to be seen quickly are prioritized.",
      name: "Jeanette Collins",
      title: "Senior Dermatology Nurse, Southern Health & Social Care Trust",
      logo: "/Image/CustomerLogo/NHS.png"
    },
    {
      quote: "With EceptionistCX, we can finally see the full picture of each inmate’s healthcare journey. It’s improved access and accountability across the board.",
      name: "Louisiana Department of Corrections",
      title: "Medical Director",
      logo: "/Image/CustomerLogo/LDOC.png"
    },
  ];

  const metrics = [
    { value: "25+", label: "Years Experience" },
    { value: "10M+", label: "Patients Impacted" },
    { value: "1,000+", label: "Healthcare Facilities" },
    { value: "99.9%", label: "Uptime SLA" },
  ];

  const trusted = [
    { label: "HIPAA (Compliant)", Icon: ShieldCheck },
    { label: "SOC 2 (Type II)", Icon: ShieldCheck },
    { label: "GDPR (Ready)", Icon: ShieldCheck },
    { label: "HL7 FHIR", Icon: ShieldCheck },
  ];

  const TestimonialsStrip = ({ testimonials }: { testimonials: any[] }) => {
    const [index, setIndex] = useState(0);
    const total = testimonials.length;
    const wrap = (i: number) => (i + total) % total;
    const prev = () => setIndex((i) => wrap(i - 1));
    const next = () => setIndex((i) => wrap(i + 1));
    const visible = [0, 1, 2].map((o) => testimonials[wrap(index + o)]);

    return (
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visible.map((t, i) => (
            <Card key={`${t.name}-${i}`} className="bg-gray-50 border-gray-100 h-full">
              <CardContent className="p-8 text-left flex flex-col justify-between h-full">
                <div>
                  <Quote className="w-8 h-8 text-blue-600 mb-4" />
                  <p className="text-lg italic text-gray-700 mb-6">"{t.quote}"</p>
                </div>
                <div className="flex items-center justify-between mt-auto">
                  <div>
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <p className="text-sm text-gray-600">{t.title}</p>
                  </div>
                  <img src={t.logo} alt="Company logo" className="h-6" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <button
          aria-label="Previous testimonials"
          onClick={prev}
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 p-2 rounded-full bg-white border border-gray-200 shadow hover:shadow-md"
        >
          ‹
        </button>
        <button
          aria-label="Next testimonials"
          onClick={next}
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 p-2 rounded-full bg-white border border-gray-200 shadow hover:shadow-md"
        >
          ›
        </button>
        <div className="mt-4 flex justify-center gap-2">
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === index ? "bg-blue-700" : "bg-gray-300"}`} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden h-screen w-full">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@800;900&display=swap');
        `}</style>
        {/* Background gradient */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(1000px 520px at 20% 22%, rgba(255,255,255,0.18), transparent 60%)," +
              "radial-gradient(900px 480px at 88% 48%, rgba(255,255,255,0.12), transparent 65%)," +
              "linear-gradient(112deg, #724A8C 0%, #B3517B 34%, #D2714F 62%, #E2C34C 100%)",
          }}
        />

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center">
          <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
            {/* Left text */}
            <div className="flex-1 max-w-2xl lg:max-w-none text-center lg:text-left">
              <h1
                className="text-[#1F2B59] font-black tracking-tight leading-[0.9] text-[clamp(2.5rem,8vw,4.5rem)] sm:text-[clamp(3rem,9vw,5rem)] lg:text-[clamp(3.5rem,10vw,5.5rem)]"
                style={{
                  fontFamily:
                    "Montserrat, Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
                  letterSpacing: "-0.02em",
                }}
              >
                {t('hero.title').split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < t('hero.title').split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </h1>
              <p className="mt-4 sm:mt-6 text-white/95 text-[clamp(1rem,3vw,1.25rem)] sm:text-[clamp(1.125rem,3.5vw,1.375rem)] max-w-xl mx-auto lg:mx-0">
                {t('hero.subtitle')}
              </p>
              <div className="mt-6 sm:mt-8">
                <a
                  href="#learn-more"
                  className="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 rounded-xl bg-[#1F2B59] text-white font-semibold text-[clamp(0.875rem,2.5vw,1rem)] shadow-lg hover:shadow-xl hover:bg-[#18244C] transition"
                >
                  {t('common.learnMore')}
                </a>
              </div>
            </div>

            {/* Right-side head image */}
            <div className="flex-shrink-0 lg:flex-shrink-0">
              <HeadImg className="w-[clamp(200px,40vw,420px)] sm:w-[clamp(250px,45vw,450px)] lg:w-[clamp(300px,50vw,500px)] drop-shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <SectionWrapper>
        <section id="learn-more" className="py-16 bg-gray-50 scroll-mt-24 lg:scroll-mt-28">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-4xl font-bold mb-6">Why EceptionistCX</motion.h2>
            <p className="text-gray-600 max-w-3xl mx-auto mb-12 text-lg">A trusted platform purpose-built for healthcare, combining modular tools, seamless integrations, and proven automation to simplify complex workflows.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyItems.map(({ title, desc, Icon }) => (
                <Card key={title} className="bg-gradient-to-r from-orange-200 via-pink-200 to-yellow-200 border-orange-300 h-full">
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <Icon className="w-10 h-10 text-orange-700 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">{title}</h3>
                    <p className="text-base text-gray-700">{desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </SectionWrapper>

      {/* PRODUCTS */}
      <SectionWrapper>
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between gap-4 mb-8">
              <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-4xl font-bold tracking-tight">Products Snapshot</motion.h2>
              <Link to="/service-summary" className="hidden sm:inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium">View all products <ArrowRight className="w-4 h-4"/></Link>
            </div>
            <p className="text-gray-600 max-w-3xl mb-10 text-lg">A unified platform with modular products you can adopt individually or together.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map(({ key, name, desc, Icon, badge }) => (
                <Link key={key} to={`/products/${key}`}>
                  <Card className="h-full border-gray-100 bg-white hover:border-blue-300 hover:shadow-md transition cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 rounded-full p-3 bg-blue-50">
                          <Icon className="w-6 h-6 text-blue-700" />
                        </div>
                        {badge && <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">{badge}</span>}
                      </div>
                      <h3 className="mt-4 text-2xl font-bold text-gray-900">{name}</h3>
                      <p className="mt-2 text-base text-gray-600 min-h-[44px]">{desc}</p>
                      <div className="mt-3 inline-flex items-center gap-1 text-blue-700 text-sm font-medium">Learn more <ArrowRight className="w-4 h-4" /></div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </SectionWrapper>

      {/* WHO WE SERVE */}
      <SectionWrapper>
        <section className="py-20 bg-blue-50">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">Who We Serve</h2>
            <p className="text-gray-600 max-w-3xl mx-auto mb-12 text-lg">EceptionistCX is trusted by organizations across healthcare, research, and public services.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {whoItems.map(({ name, Icon }) => (
                <div key={name} className="bg-white rounded-xl shadow-sm p-6 flex flex-col items-center text-center">
                  <div className="rounded-full p-3 bg-blue-100 mb-4"><Icon className="w-8 h-8 text-blue-700" /></div>
                  <h3 className="text-xl font-semibold text-gray-900">{name}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>
      </SectionWrapper>

      {/* LOGOS + TESTIMONIALS */}
      <SectionWrapper>
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-12">Trusted by Leading Organizations</h2>

            <div className="mb-16">
              <CustomerLogos 
                logoUids={customerLogoUids}
                mode="card"
                className="flex flex-wrap justify-center items-center gap-8 opacity-80"
                logoClassName="grayscale hover:grayscale-0 transition"
              />
            </div>

            <TestimonialsStrip testimonials={testimonials} />
          </div>
        </section>
      </SectionWrapper>

      {/* CTA */}
      <SectionWrapper>
        <section className="py-20 bg-gradient-to-r from-blue-700 to-green-600 text-white text-center">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl font-bold mb-6">Ready to simplify your workflows?</h2>
            <button 
              onClick={navigationStore.openDemoModal}
              className="bg-white text-blue-700 font-semibold px-6 py-3 rounded-full shadow hover:bg-gray-100 transition"
            >
              Book a Demo
            </button>
            <div className="flex justify-center gap-8 mt-8 text-lg">
              <div className="flex items-center gap-2"><Phone className="w-5 h-5" /> <span>1-800-994-1275</span></div>
              <div className="flex items-center gap-2"><Mail className="w-5 h-5" /> <span>info@eceptionist.com</span></div>
            </div>
          </div>
        </section>
      </SectionWrapper>

      {/* METRICS */}
      <SectionWrapper>
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {metrics.map((m, i) => (
              <div key={i}>
                <div className="text-4xl font-extrabold text-blue-700 mb-1">{m.value}</div>
                <div className="text-base md:text-lg text-gray-700">{m.label}</div>
              </div>
            ))}
          </div>
        </section>
      </SectionWrapper>

      {/* TRUSTED & SECURE */}
      <SectionWrapper>
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-8">Trusted & Secure</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 items-start">
              {trusted.map((t, i) => (
                <div key={i} className="flex flex-col items-center">
                  <t.Icon className="w-9 h-9 text-blue-700 mb-3" />
                  <p className="text-base md:text-lg font-medium text-gray-700">{t.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </SectionWrapper>
    </div>
  )
}

export default Home


